import React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWarning } from '@fortawesome/free-solid-svg-icons';
import MainButton from '../MainButton/MainButton';
import {
	registerWorkflowUi,
	WorkflowConfirmation,
} from '../../services/workflowClient';

const WorkflowHost: React.FC = () => {
	const [denied, setDenied] = React.useState<{
		message: string;
		remedy: string;
	} | null>(null);
	const [pending, setPending] = React.useState<{
		confirmation: WorkflowConfirmation;
		resolve: (answer: { choice: string; input?: string }) => void;
	} | null>(null);
	const [inputValue, setInputValue] = React.useState('');

	React.useEffect(() => {
		setInputValue('');
	}, [pending?.confirmation.rule]);

	React.useEffect(() => {
		registerWorkflowUi({
			ask: (confirmation) =>
				new Promise((resolve) => {
					setPending({ confirmation, resolve });
				}),
			deny: (message, remedy) => setDenied({ message, remedy }),
		});
	}, []);

	return (
		<>
			<Dialog.Root open={pending !== null}>
				<Dialog.Portal>
					<Dialog.Overlay className="fixed inset-0 bg-black/30" />
					<Dialog.Content
						role="alertdialog"
						className="fixed left-1/2 top-1/2 w-[28rem] max-w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2 rounded-lg bg-white p-6 shadow-lg"
						onEscapeKeyDown={(event) => event.preventDefault()}
						onPointerDownOutside={(event) => event.preventDefault()}
					>
						<Dialog.Title className="mb-2 flex items-center gap-2 text-lg font-semibold">
							<FontAwesomeIcon icon={faWarning} className="text-amber-600" />
							Confirmation requise
						</Dialog.Title>
						<Dialog.Description className="mb-4 text-sm text-gray-700">
							{pending?.confirmation.message}
						</Dialog.Description>
						{pending?.confirmation.input && (
							<label className="mb-4 block text-sm text-gray-700">
								<span className="mb-1 block font-medium">
									{pending.confirmation.input.label}
								</span>
								<input
									type="text"
									maxLength={255}
									value={inputValue}
									placeholder={pending.confirmation.input.placeholder}
									onChange={(event) => setInputValue(event.target.value)}
									className="w-full rounded border border-gray-300 px-3 py-2 text-sm text-gray-800 focus:border-indigo-500 focus:outline-none"
								/>
							</label>
						)}
						<div className="flex flex-col gap-2">
							{pending?.confirmation.choices.map((choice) => {
								const needsInput =
									choice.id !== 'cancel' &&
									Boolean(pending.confirmation.input) &&
									pending.confirmation.input?.required !== false;
								const missingInput = needsInput && inputValue.trim() === '';

								return (
									<MainButton
										key={choice.id}
										label={choice.label}
										fullWidth
										hideIcon
										disabled={missingInput}
										onClick={() => {
											if (missingInput) return;
											const resolve = pending.resolve;
											const text = inputValue.trim();
											setPending(null);
											resolve({
												choice: choice.id,
												input: text !== '' ? text : undefined,
											});
										}}
									/>
								);
							})}
						</div>
					</Dialog.Content>
				</Dialog.Portal>
			</Dialog.Root>

			<Dialog.Root
				open={denied !== null}
				onOpenChange={(open) => {
					if (!open) setDenied(null);
				}}
			>
				<Dialog.Portal>
					<Dialog.Overlay className="fixed inset-0 bg-black/30" />
					<Dialog.Content
						role="alertdialog"
						className="fixed left-1/2 top-1/2 w-[28rem] max-w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2 rounded-lg bg-white p-6 shadow-lg"
					>
						<Dialog.Title className="mb-2 flex items-center gap-2 text-lg font-semibold">
							<FontAwesomeIcon icon={faWarning} className="text-red-600" />
							Action impossible
						</Dialog.Title>
						<p className="text-sm text-gray-800">{denied?.message}</p>
						{denied?.remedy && (
							<p className="mt-2 text-sm text-gray-600">{denied.remedy}</p>
						)}
						<div className="mt-4 flex justify-end">
							<MainButton
								label="Compris"
								hideIcon
								onClick={() => setDenied(null)}
							/>
						</div>
					</Dialog.Content>
				</Dialog.Portal>
			</Dialog.Root>
		</>
	);
};

export default WorkflowHost;
